/**
 * MIT License
 *
 * Copyright (c) 2025 Chris M. Perez
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

export type TracingCategory =
	| 'layers'
	| 'router'
	| 'components'
	| 'effects'
	| 'signals'
	| 'suspense'
	| 'emit'
	| 'store'
	| 'fibers'
	| 'hooks';

export interface UseHooksCategories {
	readonly useWindowSize: boolean;
	readonly useLocalStorage: boolean;
	readonly useEventListener: boolean;
	readonly useMediaQuery: boolean;
	readonly useOnline: boolean;
	readonly useInterval: boolean;
	readonly useDebounce: boolean;
	readonly useThrottle: boolean;
}

export const defaultUseHooksCategories: UseHooksCategories = {
	useWindowSize: true,
	useLocalStorage: true,
	useEventListener: true,
	useMediaQuery: true,
	useOnline: true,
	useInterval: true,
	useDebounce: true,
	useThrottle: true,
};

export interface TracingCategories {
	readonly layers: boolean;
	readonly router: boolean;
	readonly components: boolean;
	readonly effects: boolean;
	readonly signals: boolean;
	readonly suspense: boolean;
	readonly emit: boolean;
	readonly store: boolean;
	readonly fibers: boolean;
	readonly hooks: boolean;
	readonly useHooks?: Partial<UseHooksCategories>;
}

export const defaultCategories: TracingCategories = {
	layers: true,
	router: true,
	components: true,
	effects: true,
	signals: false,
	suspense: true,
	emit: true,
	store: true,
	fibers: true,
	hooks: true,
	useHooks: defaultUseHooksCategories,
};

export const isCategoryEnabled = (
	categories: Partial<TracingCategories> | undefined,
	category: TracingCategory
): boolean => {
	if (!categories) return defaultCategories[category];
	return categories[category] ?? defaultCategories[category];
};
